import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { useRef, useState } from 'react'
import { data, Form, Link, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	AuthButton,
	AuthErrors,
	AuthFootnote,
	AuthShell,
} from '#app/components/heute/auth-shell.tsx'
import {
	heeftTweeFactor,
	maakSessie,
} from '#app/domain/telefoon-login.server.ts'
import {
	requestEmailCode,
	verifyEmailCode,
} from '#app/domain/token-auth.server.ts'
import { isThrownData } from '#app/utils/api-errors.server.ts'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getTranslator } from '#app/utils/i18n.server.ts'
import { useT } from '#app/utils/i18n.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { toonNummer } from '#app/utils/telefoon.ts'
import { type Route } from './+types/login.email.ts'
import { handleNewSession } from './login.server.ts'
import {
	aanmeldbewijsSessionKey,
	bewijsVoorMail,
} from './telefoon-deur.server.ts'

/**
 * De reserve (W2, besluit 1): inloggen met een e-mailcode, voor wie zijn
 * telefoon niet bij de hand heeft. Een getypte code, nooit een link om aan
 * te tikken (dezelfde lijn als de app, token-auth.server.ts). De code gaat
 * altijd de deur uit, ook naar een onbekend adres: zo verraadt de deur niet
 * wie er een account heeft. Wie zo binnenkomt zonder nummer, krijgt daarna
 * de rustige stap om het toe te voegen (naInloggen).
 */

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

type Stand =
	| {
			stap: 'adres'
			fout?: 'auth.email_code_failed' | 'auth.fout_tel_druk'
			email?: string
	  }
	| {
			stap: 'code'
			email: string
			fout?:
				| 'auth.error_invalid_code'
				| 'auth.fout_tel_op_slot'
				| 'auth.fout_tel_druk'
			opnieuw?: boolean
	  }

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const t = await getTranslator(request)
	// alleen wie via "Al een account?" kwam, houdt zijn bewezen nummer; elk
	// ander bezoek vergeet het (W2 review: een gedeelde computer)
	const houd = new URL(request.url).searchParams.get('houd')
	const { phone, headers } = await bewijsVoorMail(request, houd)
	return data(
		{
			metaTitle: `${t('auth.meta_title_login')} | heute`,
			bewezenNummer: phone,
		},
		{ headers },
	)
}

/**
 * Na het inloggen met je mail hecht er nooit vanzelf een nummer (W2
 * review). Heb je nog geen nummer, dan stuurt naInloggen je naar /nummer,
 * en daar vraagt de app eerst of het bewezen nummer van jou is. Heb je er
 * al een, dan gaat het bewijs gewoon weg. Bij extra beveiliging (2FA)
 * vervangt handleNewSession de verificatiekoek al, en daarmee het bewijs.
 */
async function bewijsMoetWeg(userId: string) {
	if (await heeftTweeFactor(userId)) return false
	const user = await prisma.user.findUnique({
		where: { id: userId },
		select: { phone: true },
	})
	return Boolean(user?.phone)
}

const lijktAdres = (email: string) =>
	/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 100

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const intent = formData.get('intent')
	const email = String(formData.get('email') ?? '')
		.trim()
		.toLowerCase()
	const redirectTo = formData.get('redirectTo')

	if (intent === 'stuur' || intent === 'opnieuw') {
		if (!lijktAdres(email)) {
			return data<Stand>(
				{ stap: 'adres', fout: 'auth.email_code_failed', email },
				{ status: 400 },
			)
		}
		try {
			await requestEmailCode(email)
		} catch (thrown) {
			// de grens per adres (drie per kwartier): een zin, geen foutpagina
			if (!isThrownData(thrown)) throw thrown
			return data<Stand>(
				intent === 'opnieuw'
					? { stap: 'code', email, fout: 'auth.fout_tel_druk' }
					: { stap: 'adres', fout: 'auth.fout_tel_druk', email },
				{ status: 429 },
			)
		}
		return data<Stand>({ stap: 'code', email, opnieuw: intent === 'opnieuw' })
	}

	if (intent === 'code') {
		const code = String(formData.get('code') ?? '').replace(/\D/g, '')
		let userId: string
		try {
			userId = (await verifyEmailCode(email, code)).id
		} catch (thrown) {
			if (!isThrownData(thrown)) throw thrown
			const opSlot = (thrown.data as { code?: string }).code === 'code_locked'
			return data<Stand>(
				{
					stap: 'code',
					email,
					fout: opSlot ? 'auth.fout_tel_op_slot' : 'auth.error_invalid_code',
				},
				{ status: opSlot ? 429 : 400 },
			)
		}
		return handleNewSession({
			request,
			session: await maakSessie(userId),
			remember: true,
			redirectTo:
				typeof redirectTo === 'string' && redirectTo ? redirectTo : undefined,
			// via handleNewSession, niet als eigen set-cookie: die schrijft
			// de verificatiekoek al (wachtende inlog opruimen)
			vergeetUitVerificatie: (await bewijsMoetWeg(userId))
				? [aanmeldbewijsSessionKey]
				: [],
		})
	}

	return data<Stand>({ stap: 'adres' }, { status: 400 })
}

export default function LoginEmailPage({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const t = useT()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const stand = (actionData as Stand | undefined) ?? { stap: 'adres' }
	const formRef = useRef<HTMLFormElement>(null)
	const [code, setCode] = useState('')
	const verborgen = redirectTo ? (
		<input type="hidden" name="redirectTo" value={redirectTo} />
	) : null
	const telefoonPad = redirectTo
		? `/login?${new URLSearchParams({ redirectTo })}`
		: '/login'

	if (stand.stap === 'code') {
		return (
			<AuthShell
				title={t('auth.verify_email_title')}
				sub={t('auth.app_code_sub', { email: stand.email })}
			>
				<Form method="POST" ref={formRef}>
					<HoneypotInputs />
					<input type="hidden" name="intent" value="code" />
					<input type="hidden" name="email" value={stand.email} />
					{verborgen}
					<div className="veld">
						<label htmlFor="email-code" className="veld-naam">
							{t('auth.code')}
						</label>
						<input
							id="email-code"
							name="code"
							autoComplete="one-time-code"
							inputMode="numeric"
							pattern="[0-9]{6}"
							maxLength={6}
							autoFocus
							required
							value={code}
							onChange={(e) => {
								const cijfers = e.target.value.replace(/\D/g, '').slice(0, 6)
								setCode(cijfers)
								if (cijfers.length === 6 && !isPending) {
									requestAnimationFrame(() => formRef.current?.requestSubmit())
								}
							}}
							className="veld-invoer"
							data-testid="email-code"
							aria-invalid={stand.fout ? true : undefined}
							aria-describedby={stand.fout ? 'email-code-fout' : undefined}
							style={{ letterSpacing: '0.3em', fontSize: 20 }}
						/>
						<AuthErrors
							errors={stand.fout ? [t(stand.fout)] : null}
							id="email-code-fout"
						/>
					</div>
					<AuthButton
						type="submit"
						pending={isPending}
						style={{ marginTop: 8 }}
					>
						{t('auth.login_cta')}
					</AuthButton>
				</Form>
				{stand.opnieuw && !stand.fout ? (
					<p className="zin-sub" role="status">
						{t('auth.nieuwe_code_onderweg')}
					</p>
				) : null}
				<div
					style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}
				>
					<Form method="POST">
						<HoneypotInputs />
						<input type="hidden" name="intent" value="opnieuw" />
						<input type="hidden" name="email" value={stand.email} />
						{verborgen}
						<button
							type="submit"
							className="tekst-link"
							disabled={isPending}
							style={{
								background: 'none',
								border: 0,
								padding: 0,
								cursor: 'pointer',
							}}
						>
							{t('auth.nieuwe_code')}
						</button>
					</Form>
					<Link to={telefoonPad} className="tekst-link">
						{t('auth.via_telefoon')}
					</Link>
				</div>
			</AuthShell>
		)
	}

	return (
		<AuthShell
			title={t('auth.email_login_title')}
			sub={
				loaderData.bewezenNummer
					? `${t('auth.app_sub')} ${t('auth.tel_mail_hecht', {
							phone: toonNummer(loaderData.bewezenNummer),
						})}`
					: t('auth.app_sub')
			}
		>
			<Form method="POST">
				<HoneypotInputs />
				<input type="hidden" name="intent" value="stuur" />
				{verborgen}
				<div className="veld">
					<label htmlFor="login-email" className="veld-naam">
						{t('auth.email')}
					</label>
					<input
						id="login-email"
						name="email"
						type="email"
						autoComplete="email"
						autoFocus
						required
						defaultValue={stand.email}
						className="veld-invoer"
						data-testid="login-email"
						aria-invalid={stand.fout ? true : undefined}
						aria-describedby={stand.fout ? 'login-email-fout' : undefined}
					/>
					<AuthErrors
						errors={stand.fout ? [t(stand.fout)] : null}
						id="login-email-fout"
					/>
				</div>
				<AuthButton type="submit" pending={isPending} style={{ marginTop: 8 }}>
					{t('auth.send_code')}
				</AuthButton>
			</Form>
			<AuthFootnote>
				<Link to={telefoonPad} data-testid="login-via-telefoon">
					{t('auth.via_telefoon')}
				</Link>
			</AuthFootnote>
		</AuthShell>
	)
}

export const meta: Route.MetaFunction = ({ data: d }) => {
	return [{ title: d ? d.metaTitle : 'heute' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
